import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useLanguage } from '@/lib/i18n';

const SHOW_DEV_BAR = process.env.REACT_APP_SHOW_DEV_BAR !== 'false';

/**
 * DevBanner — top-of-site status bar.
 *
 * Iter 130 — redesigned to:
 *   • Show an estimated public-launch date (Dec 31, 2026) with a live
 *     countdown (days / hours / minutes / seconds).
 *   • Swap the bright-orange palette for a deep slate gradient with an
 *     amber accent pulse so it reads as a professional status bar
 *     rather than a "construction tape" warning. The amber pulse + the
 *     countdown's mono-tabular digits keep it eye-catching without
 *     fighting the brand's emerald / purple / orange CTAs below it.
 *   • Stays a single clickable link to /changelog so visitors can
 *     follow build progress (the user wants people to "follow along
 *     with the progress of the site and review our change log").
 *   • Bilingual EN / JA.
 *
 * Target launch date is intentionally hardcoded (`LAUNCH_DATE_ISO`)
 * because it is a marketing commitment, not a feature flag. Update
 * here when the date changes.
 */
const LAUNCH_DATE_ISO = '2026-12-31T23:59:59+09:00'; // 23:59 JST Dec 31, 2026
const LAUNCH_TS = new Date(LAUNCH_DATE_ISO).getTime();

function diff(now) {
  const ms = Math.max(0, LAUNCH_TS - now);
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds, isLaunched: ms === 0 };
}

const pad = (n) => String(n).padStart(2, '0');

export default function DevBanner() {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!SHOW_DEV_BAR) return null;

  const { days, hours, minutes, seconds, isLaunched } = diff(now);

  // Locale-aware launch-date label. Using fixed strings here (rather
  // than `Intl.DateTimeFormat`) so the visible date matches the
  // marketing copy exactly.
  const launchDateLabel = ja ? '2026年12月31日' : 'Dec 31, 2026';

  return (
    <BannerShell ja={ja}>
      <div className="max-w-7xl mx-auto px-3 py-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] sm:text-xs">
        {/* Live status pulse + headline */}
        <span className="inline-flex items-center gap-1.5">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-amber-300 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" />
          </span>
          <span className="font-bold tracking-wide uppercase text-amber-200">
            {ja ? '開発状況を公開中' : 'Building publicly'}
          </span>
        </span>

        <span className="hidden sm:inline text-slate-600" aria-hidden="true">·</span>

        {/* Launch date */}
        <span className="text-slate-300">
          {ja ? '公開予定日：' : 'Estimated launch:'}{' '}
          <span className="font-bold text-white" data-testid="launch-date">{launchDateLabel}</span>
        </span>

        <span className="hidden sm:inline text-slate-600" aria-hidden="true">·</span>

        {/* Live countdown */}
        {isLaunched ? (
          <span
            className="font-bold text-emerald-300 uppercase tracking-wider"
            data-testid="launch-countdown-live"
          >
            {ja ? '公開済み 🎉' : 'Live now 🎉'}
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 font-mono tabular-nums text-white"
            data-testid="launch-countdown"
            aria-label={ja ? '公開まで' : 'Time until launch'}
          >
            <span className="inline-flex items-baseline gap-0.5" data-testid="launch-countdown-days">
              <span className="font-bold">{days}</span>
              <span className="text-[9px] text-slate-400 font-sans uppercase tracking-wider ml-0.5">{ja ? '日' : 'd'}</span>
            </span>
            <span className="text-slate-600" aria-hidden="true">:</span>
            <span className="inline-flex items-baseline gap-0.5" data-testid="launch-countdown-hours">
              <span className="font-bold">{pad(hours)}</span>
              <span className="text-[9px] text-slate-400 font-sans uppercase tracking-wider ml-0.5">{ja ? '時' : 'h'}</span>
            </span>
            <span className="text-slate-600" aria-hidden="true">:</span>
            <span className="inline-flex items-baseline gap-0.5" data-testid="launch-countdown-minutes">
              <span className="font-bold">{pad(minutes)}</span>
              <span className="text-[9px] text-slate-400 font-sans uppercase tracking-wider ml-0.5">{ja ? '分' : 'm'}</span>
            </span>
            <span className="text-slate-600" aria-hidden="true">:</span>
            <span className="inline-flex items-baseline gap-0.5" data-testid="launch-countdown-seconds">
              <span className="font-bold">{pad(seconds)}</span>
              <span className="text-[9px] text-slate-400 font-sans uppercase tracking-wider ml-0.5">{ja ? '秒' : 's'}</span>
            </span>
          </span>
        )}

        <span className="hidden sm:inline text-slate-600" aria-hidden="true">·</span>

        {/* Twin destination pills (desktop only). Each is its own real
            <Link> with stopPropagation so clicking the pill goes to
            that page, while clicking anywhere else on the row falls
            through to the BannerShell's default-to-changelog handler.
            On mobile we collapse back to a single "Follow progress"
            pill — there isn't enough horizontal room for two, and the
            destination page already has a Changelog ⇄ Roadmap tab
            toggle so users see both options once they land. */}
        <span className="hidden sm:inline-flex items-center gap-1.5">
          <Link
            to="/changelog"
            onClick={(e) => e.stopPropagation()}
            data-testid="dev-banner-changelog-link"
            className="bg-amber-400/15 hover:bg-amber-400/25 text-amber-200 border border-amber-400/30 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors"
          >
            {ja ? '更新履歴 →' : 'Changelog →'}
          </Link>
          <Link
            to="/roadmap"
            onClick={(e) => e.stopPropagation()}
            data-testid="dev-banner-roadmap-link"
            className="bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-200 border border-fuchsia-400/40 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors"
          >
            {ja ? 'ロードマップ →' : 'Roadmap →'}
          </Link>
          <Link
            to="/live-reporting"
            onClick={(e) => e.stopPropagation()}
            data-testid="dev-banner-live-reporting-link"
            className="inline-flex items-center gap-1 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-400/40 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            {ja ? 'ライブレポート →' : 'Live Reporting →'}
          </Link>
        </span>

        <Link
          to="/changelog"
          onClick={(e) => e.stopPropagation()}
          data-testid="dev-banner-mobile-link"
          className="sm:hidden bg-amber-400/15 hover:bg-amber-400/25 text-amber-200 border border-amber-400/30 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors"
        >
          {ja ? '更新履歴を見る →' : 'Follow progress →'}
        </Link>
      </div>
    </BannerShell>
  );
}

/**
 * BannerShell — clickable row that defaults to /changelog when the
 * user clicks any non-interactive part of the bar. Inner links
 * (Roadmap, Changelog pills) stop propagation so they take you to
 * their specific destination instead.
 */
function BannerShell({ ja, children }) {
  const navigate = useNavigate();
  const goChangelog = () => navigate('/changelog');
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={goChangelog}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goChangelog();
        }
      }}
      className="hidden sm:block bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 hover:from-slate-900 hover:via-slate-800 hover:to-slate-900 text-slate-100 transition-colors cursor-pointer border-b border-slate-800 focus:outline-none focus:ring-1 focus:ring-amber-400/40"
      data-testid="dev-banner"
      aria-label={ja ? '更新履歴を開く' : 'Open changelog'}
    >
      {children}
    </div>
  );
}
